import { Phone, Mail, MapPin, Facebook, Linkedin, CheckCircle, Award, Shield } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { openOpenSolar } from "@/lib/widgets";
import { track } from "@/lib/analytics";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useState } from "react";
export function Footer() {
  const {
    toast
  } = useToast();
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) {
      toast({
        title: "Email vereist",
        description: "Vul alstublieft uw e-mailadres in.",
        variant: "destructive"
      });
      return;
    }
    setIsSubmitting(true);
    try {
      const {
        data,
        error
      } = await supabase.functions.invoke('send-newsletter', {
        body: {
          email,
          type: 'subscribe'
        }
      });
      if (error) throw error;
      track("newsletter_signup", {
        source: "footer"
      });
      toast({
        title: "Succesvol aangemeld!",
        description: "Bedankt voor uw aanmelding. U ontvangt een bevestigingsmail."
      });
      setEmail("");
    } catch (error: any) {
      console.error('Newsletter signup error:', error);
      toast({
        title: "Er ging iets mis",
        description: "Probeer het later opnieuw of neem contact met ons op.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  return <footer className="relative z-10 pb-10 pt-20 lg:pb-20 lg:pt-[120px] dark:bg-dark rounded-none bg-[#4a69ca]/[0.38]">
      <div className="container mx-auto">
        <div className="-mx-4 flex flex-wrap">
          {/* Main Company Section */}
          <div className="w-full px-4 sm:w-2/3 lg:w-3/12">
            <div className="mb-10 w-full">
              <Link to="/" className="mb-6 inline-block max-w-[160px]">
                <h3 className="text-2xl font-bold text-dark text-indigo-800">SOL-VI</h3>
              </Link>
              <p className="mb-7 text-base text-body-color dark:text-dark-6 text-slate-950">
                Specialist in zonnepanelen, warmtepompen, thuisbatterijen en laadpalen. 
                Duurzame energieoplossingen in Elst en regio Gelderland.
              </p>
              <p className="flex items-center text-sm font-medium text-dark dark:text-white">
                <span className="mr-3 text-primary">
                  <Phone className="h-5 w-5" />
                </span>
                <a href="tel:0617105786" onClick={() => track("contact", {
                contact_method: "phone",
                source: "footer"
              })}>
                  06 17 10 57 86
                </a>
              </p>
            </div>
          </div>

          {/* Hoofddiensten */}
          <div className="w-full px-4 sm:w-1/2 lg:w-2/12">
            <div className="mb-10 w-full">
              <h4 className="mb-9 text-lg font-semibold text-dark dark:text-white">
                Hoofddiensten
              </h4>
              <ul className="space-y-3">
                <li>
                  <Link to="/zonnepanelen" className="inline-block text-base leading-loose text-body-color hover:text-primary dark:text-dark-6" onClick={() => track("footer_link", {
                  link: "zonnepanelen"
                })}>
                    Zonnepanelen
                  </Link>
                </li>
                <li>
                  <Link to="/warmtepompen" className="inline-block text-base leading-loose text-body-color hover:text-primary dark:text-dark-6" onClick={() => track("footer_link", {
                  link: "warmtepompen"
                })}>
                    Warmtepompen
                  </Link>
                </li>
                <li>
                  <Link to="/thuisbatterijen" className="inline-block text-base leading-loose text-body-color hover:text-primary dark:text-dark-6" onClick={() => track("footer_link", {
                  link: "thuisbatterijen"
                })}>
                    Thuisbatterijen
                  </Link>
                </li>
                <li>
                  <Link to="/laadpalen" className="inline-block text-base leading-loose text-body-color hover:text-primary dark:text-dark-6" onClick={() => track("footer_link", {
                  link: "laadpalen"
                })}>
                    Laadpalen
                  </Link>
                </li>
              </ul>
            </div>
          </div>

          {/* Tools & Informatie */}
          <div className="w-full px-4 sm:w-1/2 lg:w-2/12">
            <div className="mb-10 w-full">
              <h4 className="mb-9 text-lg font-semibold text-dark dark:text-white">
                Tools
              </h4>
              <ul className="space-y-3">
                <li>
                  <Link to="/besparingscalculator" className="inline-block text-base leading-loose text-body-color hover:text-primary dark:text-dark-6" onClick={() => track("footer_link", {
                  link: "besparingscalculator"
                })}>
                    Besparingscalculator
                  </Link>
                </li>
                <li>
                  <Link to="/isde-subsidie-calculator" className="inline-block text-base leading-loose text-body-color hover:text-primary dark:text-dark-6" onClick={() => track("footer_link", {
                  link: "isde-calculator"
                })}>
                    ISDE Calculator
                  </Link>
                </li>
                <li>
                  <Link to="/installatie-registreren" className="inline-block text-base leading-loose text-body-color hover:text-primary dark:text-dark-6" onClick={() => track("footer_link", {
                  link: "installatie-registreren"
                })}>
                    Installatie Registratie
                  </Link>
                </li>
                <li>
                  <a href="#" onClick={e => {
                  e.preventDefault();
                  track("cta_offerte_footer_opensolar");
                  openOpenSolar();
                }} className="inline-block text-base leading-loose text-body-color hover:text-primary dark:text-dark-6">
                    3D Dakontwerp Tool
                  </a>
                </li>
              </ul>
            </div>
          </div>

          {/* Support & Informatie */}
          <div className="w-full px-4 sm:w-1/2 lg:w-2/12">
            <div className="mb-10 w-full">
              <h4 className="mb-9 text-lg font-semibold text-dark dark:text-white">
                Support
              </h4>
              <ul className="space-y-3">
                <li>
                  <Link to="/over-ons" className="inline-block text-base leading-loose text-body-color hover:text-primary dark:text-dark-6" onClick={() => track("footer_link", {
                  link: "over-ons"
                })}>
                    Over SOL-VI
                  </Link>
                </li>
                <li>
                  <Link to="/contact" className="inline-block text-base leading-loose text-body-color hover:text-primary dark:text-dark-6" onClick={() => track("footer_link", {
                  link: "contact"
                })}>
                    Contact & Support
                  </Link>
                </li>
                <li>
                  <Link to="/kennisbank" className="inline-block text-base leading-loose text-body-color hover:text-primary dark:text-dark-6" onClick={() => track("footer_link", {
                  link: "kennisbank"
                })}>
                    Knowledge Hub
                  </Link>
                </li>
                <li>
                  <Link to="/privacy-policy" className="inline-block text-base leading-loose text-body-color hover:text-primary dark:text-dark-6" onClick={() => track("footer_link", {
                  link: "privacy"
                })}>
                    Privacy & Cookies
                  </Link>
                </li>
              </ul>
            </div>
          </div>

          {/* Newsletter & Social */}
          <div className="w-full px-4 sm:w-1/2 lg:w-3/12">
            <div className="mb-10 w-full">
              <h4 className="mb-9 text-lg font-semibold text-dark text-violet-800">
                Blijf op de hoogte
              </h4>
              
              {/* Newsletter Form */}
              <form onSubmit={handleNewsletterSubmit} className="mb-6">
                <div className="mb-4">
                  <Input type="email" placeholder="Uw e-mailadres" className="w-full rounded border-stroke px-4 py-3 text-base font-medium text-dark outline-none focus:border-primary dark:border-dark-3 dark:bg-dark-2 dark:text-white dark:focus:border-primary" value={email} onChange={e => setEmail(e.target.value)} disabled={isSubmitting} required />
                </div>
                <Button type="submit" className="w-full bg-primary py-3 text-base font-medium text-white transition hover:bg-primary/90" disabled={isSubmitting}>
                  {isSubmitting ? "Bezig..." : "Aanmelden"}
                </Button>
              </form>

              {/* Social Media */}
              <div className="mb-6 flex items-center">
                <a href="https://www.facebook.com/profile.php?id=61565954054747" className="mr-3 flex h-8 w-8 items-center justify-center rounded-full border border-stroke text-dark hover:border-primary hover:bg-primary hover:text-white sm:mr-4 lg:mr-3 xl:mr-4 dark:border-dark-3 dark:text-white dark:hover:border-primary" target="_blank" rel="noopener noreferrer" onClick={() => track('outbound_click', {
                url: 'https://www.facebook.com/profile.php?id=61565954054747',
                link_text: 'Facebook'
              })}>
                  <Facebook className="h-4 w-4" />
                </a>
                <a href="https://www.linkedin.com/company/sol-vi/?viewAsMember=true" className="mr-3 flex h-8 w-8 items-center justify-center rounded-full border border-stroke text-dark hover:border-primary hover:bg-primary hover:text-white sm:mr-4 lg:mr-3 xl:mr-4 dark:border-dark-3 dark:text-white dark:hover:border-primary" target="_blank" rel="noopener noreferrer" onClick={() => track('outbound_click', {
                url: 'https://www.linkedin.com/company/sol-vi/?viewAsMember=true',
                link_text: 'LinkedIn'
              })}>
                  <Linkedin className="h-4 w-4" />
                </a>
                <a href="mailto:info@sol-vi.nl" className="mr-3 flex h-8 w-8 items-center justify-center rounded-full border border-stroke text-dark hover:border-primary hover:bg-primary hover:text-white sm:mr-4 lg:mr-3 xl:mr-4 dark:border-dark-3 dark:text-white dark:hover:border-primary" onClick={() => track("contact", {
                contact_method: "email",
                source: "footer"
              })}>
                  <Mail className="h-4 w-4" />
                </a>
              </div>
              
              <p className="text-base text-body-color dark:text-dark-6">
                &copy; 2024 SOL-VI | KVK: 87825759 | BTW: NL004489833B30
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Background Decorative Elements */}
      <div>
        <span className="absolute bottom-0 left-0 z-[-1]">
          <svg width="217" height="229" viewBox="0 0 217 229" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M-64 140.5C-64 62.904 -1.096 1.90666e-05 76.5 1.22829e-05C154.096 5.49924e-06 217 62.904 217 140.5C217 218.096 154.096 281 76.5 281C-1.09598 281 -64 218.096 -64 140.5Z" fill="url(#paint0_linear_1179_5)" />
            <defs>
              <linearGradient id="paint0_linear_1179_5" x1="76.5" y1="281" x2="76.5" y2="1.22829e-05" gradientUnits="userSpaceOnUse">
                <stop stopColor="#3056D3" stopOpacity="0.08" />
                <stop offset="1" stopColor="#C4C4C4" stopOpacity="0" />
              </linearGradient>
            </defs>
          </svg>
        </span>
        <span className="absolute right-10 top-10 z-[-1]">
          <svg width="75" height="75" viewBox="0 0 75 75" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M37.5 -1.63918e-06C58.2107 -2.54447e-06 75 16.7893 75 37.5C75 58.2107 58.2107 75 37.5 75C16.7893 75 -7.33885e-07 58.2107 -1.63918e-06 37.5C-2.54447e-06 16.7893 16.7893 -7.33885e-07 37.5 -1.63918e-06Z" fill="url(#paint0_linear_1179_4)" />
            <defs>
              <linearGradient id="paint0_linear_1179_4" x1="-1.63917e-06" y1="37.5" x2="75" y2="37.5" gradientUnits="userSpaceOnUse">
                <stop stopColor="#13C296" stopOpacity="0.31" />
                <stop offset="1" stopColor="#C4C4C4" stopOpacity="0" />
              </linearGradient>
            </defs>
          </svg>
        </span>
      </div>
    </footer>;
}